<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import { gaiaAccent, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** In-app route: renders a RouterLink (a real <a>, so new-tab/cmd-click work). */
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined,
  },
  /** Renders an <a> when set; otherwise a <button> for JS-handled actions. */
  href: {
    type: String,
    default: undefined,
  },
  underline: {
    type: Boolean,
    default: true,
  },
  /**
   * Inert and dimmed — the verify screen's resend link during its cooldown.
   * Only meaningful on the button form; a disabled <a> is not a thing, so a
   * link with an `href` ignores it.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * `accent` is the violet navigation link. `muted` is the quieter one the auth
   * screens use for "Sign out" — a way out of the flow rather than a step
   * through it, so it must not compete with the primary action above it.
   */
  tone: {
    type: String as PropType<'accent' | 'muted'>,
    default: 'accent',
  },
});

const isLink = computed(() => Boolean(props.to ?? props.href));

const accent = gaiaAccent.base;
const hover = gaiaAccent.strong;
// The element is an <a>/<button> with a :hover, so the typography reaches it
// through v-bind rather than GaiaText props.
const fontSize = gaiaFontSize.dense;
const fontWeight = String(gaiaFontWeight.semibold);
const mutedColor = gaiaNeutral.textMuted;
const subtle = gaiaNeutral.textSubtle;
const subtleHover = gaiaNeutral.text;
const medium = String(gaiaFontWeight.medium);
</script>

<template>
  <!-- Accent navigation link from the Login mock ("Forgot password?", terms).
       Real navigation — unlike the text-type GaiaButton, which is an action. -->
  <component
    :is="props.to ? RouterLink : props.href ? 'a' : 'button'"
    class="gaia-link"
    :class="{
      underline: props.underline,
      disabled: props.disabled && !isLink,
      muted: props.tone === 'muted',
    }"
    :to="props.to"
    :href="props.to ? undefined : props.href"
    :type="isLink ? undefined : 'button'"
    :disabled="isLink ? undefined : props.disabled || undefined"
  >
    <slot />
  </component>
</template>

<style scoped>
.gaia-link {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  color: v-bind(accent);
  cursor: pointer;
  text-decoration: none;
}

.gaia-link.underline {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.gaia-link:hover {
  color: v-bind(hover);
}

.gaia-link.muted {
  font-weight: v-bind(medium);
  color: v-bind(subtle);
}

.gaia-link.muted:hover {
  color: v-bind(subtleHover);
}

.gaia-link.disabled,
.gaia-link.disabled:hover {
  color: v-bind(mutedColor);
  cursor: default;
  text-decoration: none;
}
</style>
